var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { CompositeDisposable } from "./lifecycle";
import { List } from "./list";
import { TreeModel } from "./treeModel";
var Tree = /** @class */ (function () {
    function Tree(options) {
        var _this = this;
        this._nodes = new Map();
        this.nodesByIdentity = new Map();
        this.identityProvider = options === null || options === void 0 ? void 0 : options.identity;
        this._list = new List();
        this._model = new TreeModel(this._list, null, {
            collapseByDefault: options.collapseByDefault,
            autoExpandSingleChildren: true,
        });
        this._disposable = new CompositeDisposable(this._model.onDidFilterChange(function () {
            _this.rerender();
        }));
    }
    Object.defineProperty(Tree.prototype, "model", {
        get: function () {
            return this._model;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Tree.prototype, "list", {
        get: function () {
            return this._list;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Tree.prototype, "size", {
        get: function () {
            return this._nodes.size;
        },
        enumerable: false,
        configurable: true
    });
    Tree.prototype.getNodeByElement = function (element) {
        return this._nodes.get(element);
    };
    Tree.prototype.getNodeByIdentity = function (id) {
        if (!this.identityProvider) {
            throw new Error("You must provide an identityProvider to use getNodeByIdentity(...)");
        }
        return this.nodesByIdentity.get(id);
    };
    Tree.prototype.dispose = function () {
        this._disposable.dispose();
        this._model.dispose();
        this._list.dispose();
    };
    Tree.prototype.rerender = function () {
        var _a;
        var list = this.model.asList();
        (_a = this.list).splice.apply(_a, __spreadArray([0, this.list.length], __read(list)));
    };
    Tree.prototype.splice = function (location, deleteCount, toInsert) {
        var _this = this;
        var insertedElements = new Set();
        var insertedElementIds = new Set();
        var _onDidCreateNode = function (node) {
            if (node.element === null) {
                return;
            }
            if (_this.identityProvider) {
                var id = _this.identityProvider.getId(node.element).toString();
                insertedElementIds.add(id);
                _this.nodesByIdentity.set(id, node);
            }
            insertedElements.add(node.element);
            _this._nodes.set(node.element, node);
        };
        var _onDidDeleteNode = function (node) {
            if (node.element === null) {
                return;
            }
            if (!insertedElements.has(node.element)) {
                _this._nodes.delete(node.element);
            }
            if (_this.identityProvider) {
                var id = _this.identityProvider.getId(node.element).toString();
                if (!insertedElementIds.has(id)) {
                    _this.nodesByIdentity.delete(id);
                }
            }
        };
        return this._model.splice(__spreadArray(__spreadArray([], __read(location)), [0]), deleteCount, toInsert, _onDidCreateNode, _onDidDeleteNode);
    };
    Tree.prototype.setChildren = function (children, element) {
        var location = element === undefined ? [] : this.getElementLocation(element);
        this._setChildren(location, this.preserveCollapseState(children));
    };
    Tree.prototype.preserveCollapseState = function (elements) {
        var _this = this;
        if (elements === undefined) {
            return [];
        }
        return elements.map(function (treeElement) {
            var node = _this._nodes.get(treeElement.element);
            if (!node && _this.identityProvider) {
                var id = _this.identityProvider.getId(treeElement.element).toString();
                node = _this.nodesByIdentity.get(id);
            }
            if (!node) {
                return __assign(__assign({}, treeElement), { children: _this.preserveCollapseState(treeElement.children) });
            }
            var collapsible = typeof treeElement.collapsible === "boolean"
                ? treeElement.collapsible
                : node.collapsible;
            var collapsed = typeof treeElement.collapsed !== "undefined"
                ? treeElement.collapsed
                : node.collapsed;
            return __assign(__assign({}, treeElement), { collapsible: collapsible,
                collapsed: collapsed, children: _this.preserveCollapseState(treeElement.children) });
        });
    };
    Tree.prototype._setChildren = function (location, children) {
        var _this = this;
        var insertedElements = new Set();
        var insertedElementIds = new Set();
        var _onDidCreateNode = function (node) {
            if (node.element === null) {
                return;
            }
            if (_this.identityProvider) {
                var id = _this.identityProvider.getId(node.element).toString();
                insertedElementIds.add(id);
                _this.nodesByIdentity.set(id, node);
            }
            insertedElements.add(node.element);
            _this._nodes.set(node.element, node);
        };
        var _onDidDeleteNode = function (node) {
            if (node.element === null) {
                return;
            }
            if (!insertedElements.has(node.element)) {
                _this._nodes.delete(node.element);
            }
            if (_this.identityProvider) {
                var id = _this.identityProvider.getId(node.element).toString();
                if (!insertedElementIds.has(id)) {
                    _this.nodesByIdentity.delete(id);
                }
            }
        };
        this._model.splice(__spreadArray(__spreadArray([], __read(location)), [0]), Number.MAX_VALUE, children, _onDidCreateNode, _onDidDeleteNode);
    };
    Tree.prototype.getElementLocation = function (element) {
        if (element === null) {
            return [];
        }
        var node = this._nodes.get(element);
        if (!node && this.identityProvider) {
            node = this.nodesByIdentity.get(this.identityProvider.getId(element));
        }
        if (!node) {
            throw new Error("Tree element not found: " + element);
        }
        return this._model.getNodeLocation(node);
    };
    return Tree;
}());
export { Tree };
